/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PercentAmount } from '@akropolis-web/primitives';
import * as R from 'ramda';
import { ChartsGrid, LineChart, lineElementClasses } from '@mui/x-charts';

import { findClosestByTimestamp, isCriticalPriceDeviation } from 'domain/utils';
import { PriceHistory } from 'domain/types';
import { MaybeEither, toEither } from 'utils/either';
import { colors, theme } from 'core/styles';

import { priceFormatter, xTickFormatter } from './helpers';
import { ChartTooltip } from './ChartTooltip';

type Props = {
  oracle: PriceHistory;
  market: PriceHistory;
  deviation: MaybeEither<PercentAmount>;
};

export function PriceHistoryChart(props: Props) {
  const mountedRef = useRef<boolean>(false);
  const [, setRerenderTrigger] = useState<number>(0);

  // The useEffect is needed to draw the graph during the first render
  // (there is a bug in the graph related to react-spring: during
  // the first render, the width is not set in the clip-path)
  useEffect(() => {
    setTimeout(() => {
      mountedRef.current && setRerenderTrigger(current => current + 1);
    }, 300);
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const { oracle, market } = props;
  const deviation = toEither(props.deviation);

  const sortByTimestamp = R.sort<PriceHistory[number]>(R.ascend(R.prop('timestamp')));
  const oraclePriceHistory = sortByTimestamp(oracle);
  const marketPriceHistory = sortByTimestamp(market);

  const chartData: Array<{
    date: Date;
    market: number | null;
    oracle: number | null;
  }> = useMemo(() => {
    const allTimes = R.pipe(
      sortByTimestamp,
      R.pluck('timestamp'),
      R.uniq,
    )([...oraclePriceHistory, ...marketPriceHistory]);

    return allTimes.map(time => {
      const marketPrice = findClosestByTimestamp(time, marketPriceHistory);
      const oraclePrice = findClosestByTimestamp(time, oraclePriceHistory);
      return {
        date: new Date(time),
        market: marketPrice?.price || null,
        oracle: oraclePrice?.price || null,
      };
    });
  }, [oraclePriceHistory, marketPriceHistory, sortByTimestamp]);

  const { date: minTime } = chartData[0] || {};
  const { date: maxTime } = chartData[chartData.length - 1] || {};

  const minPrice = Math.min(...R.pluck('price', oracle.concat(market)));
  const maxPrice = Math.max(...R.pluck('price', oracle.concat(market)));

  return (
    <LineChart
      dataset={chartData}
      margin={{ top: 10, right: 0, left: 0, bottom: 50 }}
      series={[
        {
          label: 'Market',
          type: 'line',
          showMark: false,
          dataKey: 'market',
          color: '#86ffd3',
        },
        {
          label: 'Oracle',
          type: 'line',
          showMark: false,
          dataKey: 'oracle',
          color: deviation.fold(
            d => (isCriticalPriceDeviation(d) ? '#ff696e' : '#7325ff'),
            () => '#7325ff',
          ),
        },
      ]}
      xAxis={[
        {
          scaleType: 'time',
          dataKey: 'date',
          min: minTime,
          max: maxTime,
          valueFormatter: (value: Date) => xTickFormatter(value),
          tickNumber: 5,
        },
      ]}
      yAxis={[
        { min: minPrice, max: maxPrice, valueFormatter: (value: number) => priceFormatter(value) },
      ]}
      slots={{
        axisContent: ChartTooltip,
      }}
      sx={{
        [`& .MuiChartsAxis-top .MuiChartsAxis-line`]: {
          stroke: colors.blackOpacity[12],
        },
        [`& .${lineElementClasses.root}`]: {
          strokeWidth: 2,
        },
        [`& .MuiChartsAxisHighlight-root`]: {
          strokeDasharray: 'none',
          stroke: colors.black,
        },
        '& .MuiChartsLegend-root': {
          transform: 'translate(-10px, 16px)',
          '& .MuiChartsLegend-mark': {
            rx: 5,
          },
        },
        '& .MuiChartsGrid-line': {
          stroke: colors.blackOpacity[12],
        },
      }}
      leftAxis={{
        tickLabelStyle: {
          textAnchor: 'start',
          dominantBaseline: 'auto',
          transform: 'translate(10px, -2px)',
          fontFamily: theme.typography.secondaryFontFamily,
        },
        disableTicks: true,
      }}
      bottomAxis={{
        tickLabelStyle: {
          textAnchor: 'start',
          fontFamily: theme.typography.secondaryFontFamily,
        },
        disableTicks: true,
      }}
      topAxis={{
        disableLine: false,
        disableTicks: true,
        tickLabelStyle: {
          display: 'none',
        },
      }}
      slotProps={{
        legend: {
          labelStyle: {
            fontSize: 12,
            fontFamily: theme.typography.secondaryFontFamily,
          },
          position: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          itemMarkWidth: 10,
          itemMarkHeight: 10,
          markGap: 5,
          itemGap: 20,
        },
      }}
    >
      <ChartsGrid vertical />
      <ChartsGrid horizontal />
    </LineChart>
  );
}
